import { useEffect, useState, useMemo } from "react";
import Reveal from "@/components/Reveal";
import PitchDeckDialog from "@/components/PitchDeckDialog";
import VillaWaitlistForm from "@/components/VillaWaitlistForm";
import { fetchProjects, fetchReignVillaDetails } from "@/lib/api";
import { ArrowRight, Download, FileText, Check } from "lucide-react";

const FILTERS = ["All", "Villa Development"];

export default function Projects({ onOpenConsult }) {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [pitchOpen, setPitchOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchProjects()
      .then((data) => {
        if (cancelled) return;
        setProjects(data);
        if (data.length > 0) setActive(data[0]);
      })
      .catch(() => {
        /* fail silently — the empty state below handles it */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(
    () => (filter === "All" ? projects : projects.filter((p) => p.type === filter)),
    [filter, projects]
  );

  return (
    <div data-testid="projects-page" className="bg-[#0A1628]">
      {/* HEADER */}
      <section data-testid="projects-header" className="pt-40 pb-16 md:pb-20 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-2">
            <div className="overline">Projects</div>
          </div>
          <div className="lg:col-span-10">
            <h1 className="font-serif-display text-6xl md:text-8xl lg:text-[9rem] text-[#F3EFE4] leading-[0.88]">
              Selected work<br />
              <span className="italic text-[#D4AF37]">2026 — 2027.</span>
            </h1>
          </div>
        </div>
      </section>

      {/* PITCH DECK CTA BANNER */}
      <section data-testid="projects-pitch-banner" className="px-6 md:px-12 lg:px-16 pb-12 md:pb-16">
        <div className="max-w-[1400px] mx-auto border border-white/10 bg-gradient-to-r from-[#12253D] to-[#0A1628] p-8 md:p-10 grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div className="flex items-start gap-5">
            <div className="hidden md:flex w-14 h-14 border border-[#D4AF37] items-center justify-center shrink-0">
              <FileText className="text-[#D4AF37]" size={22} />
            </div>
            <div>
              <div className="overline mb-2">Investor brief · 2026</div>
              <h3 className="font-serif-display text-2xl md:text-3xl text-[#F3EFE4] leading-[1.05]">
                Download our <span className="italic text-[#D4AF37]">one-page pitch deck</span>.
              </h3>
              <p className="text-sm text-[#8FA3B8] mt-2 max-w-xl">
                Thesis, returns history, market snapshot, and how U-NEXX works with investor capital.
              </p>
            </div>
          </div>
          <button data-testid="projects-pitch-cta" onClick={() => setPitchOpen(true)} className="btn-primary justify-center">
            Get the Deck <Download size={16} />
          </button>
        </div>
      </section>

      {/* FILTER BAR */}
      <section className="px-6 md:px-12 lg:px-16 border-y border-white/10">
        <div className="max-w-[1400px] mx-auto flex items-center gap-2 md:gap-6 overflow-x-auto py-5">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(f)}
              className={`text-[0.72rem] tracking-[0.24em] uppercase whitespace-nowrap px-3 py-2 transition border ${
                filter === f
                  ? "border-[#D4AF37] text-[#D4AF37]"
                  : "border-transparent text-[#8FA3B8] hover:text-[#F3EFE4]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      {/* GRID */}
      <section data-testid="projects-grid" className="py-16 md:py-24 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto">
          {filtered.length === 0 ? (
            <p className="text-[#8FA3B8]">No projects match this filter yet.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filtered.map((p, i) => (
                <Reveal key={p.id} delay={i % 3}>
                  <button
                    type="button"
                    data-testid={`project-card-${p.id}`}
                    onClick={() => setActive(p)}
                    className="group block text-left w-full"
                  >
                    <div className="zoom-wrap aspect-[4/5] mb-5 relative">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                      <div className="absolute top-4 left-4 px-3 py-1 bg-[#0A1628]/80 backdrop-blur-sm text-[0.6rem] tracking-[0.22em] uppercase text-[#D4AF37]">
                        {p.status}
                      </div>
                    </div>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="overline mb-2 !text-[#8FA3B8] !text-[0.6rem]">{p.type} · {p.year}</div>
                        <h3 className="font-serif-display text-2xl md:text-3xl text-[#F3EFE4] group-hover:text-[#D4AF37] transition">
                          {p.title}
                        </h3>
                        <div className="text-xs text-[#8FA3B8] mt-1">{p.location}</div>
                      </div>
                      <ArrowRight size={18} className="text-[#8FA3B8] group-hover:text-[#D4AF37] group-hover:translate-x-1 transition shrink-0 mt-1" />
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FEATURED DETAIL */}
      {active && (
        <section data-testid="projects-detail" className="py-16 md:py-24 px-6 md:px-12 lg:px-16 bg-[#0F1F35]">
          <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="zoom-wrap aspect-[16/11]">
                <img src={active.image} alt={active.title} className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="lg:col-span-7 lg:pl-6">
              <div className="overline mb-4">Project spotlight</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-[#F3EFE4] mb-4 leading-[0.95]">
                {active.title}
              </h2>
              <div className="text-sm text-[#8FA3B8] mb-6">
                {active.location} · {active.type} · {active.year}
              </div>
              <p className="text-[#8FA3B8] leading-relaxed mb-8">{active.description}</p>

              <div className="grid grid-cols-3 border-t border-l border-white/10 mb-8">
                {Object.entries(active.metrics).map(([k, v]) => (
                  <div key={k} className="border-r border-b border-white/10 p-4">
                    <div className="text-[0.6rem] tracking-[0.22em] uppercase text-[#8FA3B8] mb-2">
                      {k.replace(/_/g, " ")}
                    </div>
                    <div className="font-serif-display text-2xl text-[#D4AF37]">{v}</div>
                  </div>
                ))}
              </div>

              {active.id === "reign-villa-resorts" && <VillaWaitlistForm />}
              {active.id === "reign-villa-resorts" && <ReignVillaBlock />}

              <button data-testid="projects-detail-cta" onClick={onOpenConsult} className="btn-link">
                Discuss a similar project <span>→</span>
              </button>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="overline mb-6">Pipeline open</div>
          <h2 className="font-serif-display text-4xl md:text-6xl text-[#F3EFE4] mb-10 leading-[0.95]">
            Have a project or capital looking for <span className="italic text-[#D4AF37]">the right partner?</span>
          </h2>
          <button data-testid="projects-bottom-cta" onClick={onOpenConsult} className="btn-primary">
            Get in touch <span className="btn-arrow">→</span>
          </button>
        </div>
      </section>

      <PitchDeckDialog open={pitchOpen} onClose={() => setPitchOpen(false)} />
    </div>
  );
}

function ReignVillaBlock() {
  const [data, setData] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  useEffect(() => {
    let cancelled = false;
    fetchReignVillaDetails()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        /* fail silently — the block is optional */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  if (!data) return null;
  const current = data.gallery?.[activeImage];
  return (
    <div data-testid="reign-villa-block" className="mb-8 border border-[#D4AF37]/40 bg-[#0F1F35]/70 p-5 md:p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="overline mb-1">Reign Villa Resorts · D.C.A. #{data.dca_approval}</div>
          <div className="text-sm text-[#F3EFE4]">
            {data.villas} villas · {data.storeys_per_villa} storeys · {data.total_area_sqft} sq ft (~{data.total_area_acres} acres)
          </div>
        </div>
        <div className="text-[0.62rem] tracking-[0.22em] uppercase text-[#8FA3B8]">Parcel {data.parcel}</div>
      </div>

      {current && (
        <figure className="mb-4">
          <div className="zoom-wrap aspect-[16/10] border border-white/10 bg-[#0A1628]">
            <img
              src={current.url}
              alt={current.caption}
              className="w-full h-full object-cover"
              data-testid="reign-villa-active-image"
            />
          </div>
          <figcaption className="mt-2 text-xs text-[#8FA3B8] italic">{current.caption}</figcaption>
        </figure>
      )}

      {/* Thumbnails */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
        {data.gallery?.map((g, idx) => (
          <button
            key={g.url}
            type="button"
            data-testid={`reign-villa-thumb-${idx}`}
            onClick={() => setActiveImage(idx)}
            className={`zoom-wrap aspect-[16/10] border transition ${
              idx === activeImage ? "border-[#D4AF37]" : "border-white/10 hover:border-white/30"
            }`}
          >
            <img src={g.url} alt={g.caption} className="w-full h-full object-cover" />
          </button>
        ))}
      </div>

      {/* Amenities */}
      <div>
        <div className="overline mb-3">Resort amenities</div>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
          {data.amenities?.map((a) => (
            <li key={a} className="flex items-start gap-2 text-xs text-[#F3EFE4]">
              <Check size={12} className="text-[#D4AF37] shrink-0 mt-1" />
              <span>{a}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Legacy component name kept in case anything imports it externally
function StLuciaLotsBlock() {
  return <ReignVillaBlock />;
}
